/**
 * Sidebar — primary navigation. Three responsive states:
 *
 *  - Desktop (>= 1280px): full sidebar with labels. User-toggle persists collapsed
 *    state to an icon-rail at 72px.
 *  - Tablet (>= 768px and < 1280px): always icon-rail (72px), static. No drawer.
 *    Labels shown via native `title` tooltip on hover.
 *  - Mobile (< 768px): off-canvas drawer, hamburger trigger lives in AppShell.
 *    Tapping a nav item triggers `onClose` from the parent.
 *
 * Active route shows a left accent rail + tinted background. Animations capped
 * at 200ms ease-out per the design tokens.
 */
import { Link, useRouterState } from '@tanstack/react-router';
import {
  LayoutDashboard,
  Users,
  Building2,
  DoorOpen,
  ShieldCheck,
  ClipboardList,
  UserCheck,
  Megaphone,
  MessageCircle,
  MessagesSquare,
  LifeBuoy,
  Wrench,
  Receipt,
  CreditCard,
  Calendar,
  FileText,
  ClipboardCheck,
  ScrollText,
  Settings,
  LogOut,
  ChevronsLeft,
  ChevronsRight,
  CircleUserRound,
  Car,
  UserCog,
  Briefcase,
  ArrowLeftRight,
  FileSignature,
  Vote,
  Trophy,
  HardHat,
  Shield,
  Tags,
  Wallet,
  HandCoins,
  AlertTriangle,
  AlertOctagon,
  ZapOff,
  BarChart3,
  FileBarChart,
  ParkingCircle,
  CarFront,
  Zap,
  Video,
  CalendarHeart,
  BookUser,
  X
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { LogoMark, Wordmark } from '@/components/brand/Logo';
import { useBranding, useBrandLogoBlobUrl } from '@/hooks/useBranding';

type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement> & { strokeWidth?: number | string }>;

type SidebarRole =
  | 'admin'
  | 'super_admin'
  | 'accountant'
  | 'treasurer'
  | 'secretary'
  | 'president'
  | 'building_manager'
  | 'resident'
  | 'guard';

interface NavItem {
  to: string;
  label: string;
  icon: IconComponent;
  /** Role allow-list. Undefined = visible to all signed-in admin-like roles. */
  roles?: ReadonlyArray<SidebarRole>;
}

interface NavSection {
  heading: string;
  items: NavItem[];
}

// Role allow-lists for nav visibility. Server enforces actual access; sidebar
// just hides modules where the role lacks meaningful permission.
//   admin            — full power
//   accountant       — finance scope (bills/ledger/documents R)
//   treasurer        — same as accountant (finance ownership)
//   secretary        — notices/community/helpdesk/documents write
//   president        — read-only across all society modules (board oversight)
//   building_manager — day-to-day ops (visitors/guards/gates/complaints/
//                      vendors/frequent-visitors/attendance/vehicles write;
//                      residents read). No bills/governance/settings/audit-log.
const ADMINS_ONLY: ReadonlyArray<SidebarRole> = ['admin', 'super_admin'];
const ADMINS_AND_MANAGER: ReadonlyArray<SidebarRole> = ['admin', 'super_admin', 'building_manager'];
const ADMINS_AND_ACCOUNTANT: ReadonlyArray<SidebarRole> = [
  'admin',
  'super_admin',
  'accountant',
  'treasurer',
  'president'
];
const ADMINS_AND_SECRETARY: ReadonlyArray<SidebarRole> = [
  'admin',
  'super_admin',
  'secretary',
  'president'
];
const ALL_ADMIN_ROOTED: ReadonlyArray<SidebarRole> = [
  'admin',
  'super_admin',
  'accountant',
  'treasurer',
  'secretary',
  'president',
  'building_manager'
];
// Staff module: admins + building manager (write) + finance + committee (read).
const STAFF_ROLES: ReadonlyArray<SidebarRole> = [
  'admin',
  'super_admin',
  'building_manager',
  'accountant',
  'treasurer',
  'secretary',
  'president'
];

// Super-admin operates cross-tenant — most society-scoped pages don't apply.
// Keep their nav minimal: tenants list (the dashboard) + future cross-tenant tools.
const SUPER_ADMIN_NAV: NavSection[] = [
  {
    heading: 'SaaS',
    items: [
      { to: '/superadmin/dashboard', label: 'Tenants', icon: Building2, roles: ['super_admin'] },
      { to: '/superadmin/billing', label: 'Billing', icon: Receipt, roles: ['super_admin'] },
      { to: '/superadmin/plans', label: 'Plans', icon: CreditCard, roles: ['super_admin'] },
      { to: '/superadmin/marketing-leads', label: 'Leads', icon: Megaphone, roles: ['super_admin'] },
      { to: '/superadmin/users', label: 'Team', icon: UserCog, roles: ['super_admin'] },
      { to: '/superadmin/hr', label: 'HR', icon: Briefcase, roles: ['super_admin'] },
      { to: '/superadmin/permissions', label: 'Permissions', icon: Shield, roles: ['super_admin'] },
      { to: '/superadmin/audit', label: 'Audit log', icon: ScrollText, roles: ['super_admin'] }
    ]
  }
];

const NAV: NavSection[] = [
  {
    heading: 'Overview',
    items: [{ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ALL_ADMIN_ROOTED }]
  },
  {
    heading: 'People',
    items: [
      { to: '/residents', label: 'Residents', icon: Users, roles: ALL_ADMIN_ROOTED },
      // Family members deliberately NOT in the sidebar — they're resident-scoped,
      // not a top-level navigation concern. Admin reaches the family list either
      // inline on the Resident Profile page OR via direct URL /family for bulk
      // ops (the route + page exist; just not surfaced in nav).
      { to: '/towers', label: 'Towers & Flats', icon: Building2, roles: ALL_ADMIN_ROOTED },
      { to: '/gates', label: 'Gates', icon: DoorOpen, roles: ADMINS_AND_MANAGER },
      { to: '/guards', label: 'Guards', icon: ShieldCheck, roles: ADMINS_AND_MANAGER },
      { to: '/agencies', label: 'Security agencies', icon: Shield, roles: ADMINS_AND_MANAGER }
    ]
  },
  {
    heading: 'Operations',
    items: [
      { to: '/panic-incidents', label: 'SOS / Panic incidents', icon: AlertOctagon, roles: ['admin', 'super_admin', 'president', 'secretary', 'building_manager'] },
      { to: '/security-incidents', label: 'Security incidents', icon: AlertOctagon, roles: ['admin', 'super_admin', 'president', 'secretary', 'building_manager'] },
      { to: '/patrol', label: 'Patrol checkpoints', icon: AlertOctagon, roles: ['admin', 'super_admin', 'building_manager', 'secretary'] },
      { to: '/chat', label: 'Guard chat', icon: MessageCircle, roles: ['admin', 'super_admin', 'president', 'secretary', 'building_manager'] },
      { to: '/cameras', label: 'CCTV cameras', icon: Video, roles: ['admin', 'super_admin', 'president', 'secretary', 'building_manager'] },
      { to: '/visitor-logs', label: 'Visitor logs', icon: ClipboardList, roles: ['admin', 'super_admin', 'president', 'building_manager'] },
      { to: '/frequent-visitors', label: 'Frequent visitors', icon: UserCheck, roles: ['admin', 'super_admin', 'president', 'building_manager'] },
      { to: '/helper-categories', label: 'Helper categories', icon: Tags, roles: ['admin', 'super_admin', 'building_manager'] },
      { to: '/attendance', label: 'Attendance', icon: ClipboardCheck, roles: ['admin', 'super_admin', 'president', 'building_manager'] },
      { to: '/notices', label: 'Notices', icon: Megaphone, roles: [...ADMINS_AND_SECRETARY, 'building_manager'] },
      { to: '/society-events', label: 'Events', icon: CalendarHeart, roles: [...ADMINS_AND_SECRETARY, 'building_manager'] },
      { to: '/community', label: 'Community', icon: MessagesSquare, roles: [...ADMINS_AND_SECRETARY, 'building_manager'] },
      { to: '/helpdesk', label: 'Helpdesk', icon: LifeBuoy, roles: [...ADMINS_AND_SECRETARY, 'building_manager'] },
      { to: '/vendors', label: 'Vendors', icon: Wrench, roles: [...ADMINS_AND_SECRETARY, 'building_manager'] },
      { to: '/service-providers', label: 'Service providers', icon: BookUser, roles: [...ADMINS_AND_SECRETARY, 'building_manager'] },
      { to: '/staff', label: 'Staff', icon: HardHat, roles: STAFF_ROLES },
      { to: '/move-in-out', label: 'Move-in/out', icon: ArrowLeftRight, roles: ADMINS_AND_SECRETARY },
      { to: '/nocs', label: 'NOCs', icon: FileSignature, roles: ADMINS_AND_SECRETARY },
      { to: '/governance', label: 'Governance', icon: Vote, roles: ADMINS_AND_SECRETARY },
      { to: '/elections', label: 'Elections', icon: Trophy, roles: ADMINS_AND_SECRETARY },
      { to: '/bills', label: 'Bills', icon: Receipt, roles: ADMINS_AND_ACCOUNTANT },
      { to: '/charge-types', label: 'Charge types', icon: Wallet, roles: ADMINS_AND_ACCOUNTANT },
      { to: '/one-time-charges', label: 'One-time charges', icon: Receipt, roles: ADMINS_AND_ACCOUNTANT },
      { to: '/receipts', label: 'Receipts', icon: FileText, roles: ADMINS_AND_ACCOUNTANT },
      { to: '/collections', label: 'Collections', icon: HandCoins, roles: ADMINS_AND_ACCOUNTANT },
      { to: '/amenities', label: 'Amenities', icon: Calendar, roles: ['admin', 'super_admin', 'president', 'building_manager'] },
      { to: '/documents', label: 'Documents', icon: FileText, roles: ALL_ADMIN_ROOTED },
      { to: '/vehicles', label: 'Vehicles', icon: Car, roles: ['admin', 'super_admin', 'president', 'building_manager'] }
    ]
  },
  // Phase 3 parking surfaces. Sibling agent registers the /parking index +
  // CRUD pages in its own Sidebar PR; these three rows are the Phase-3
  // additive surfaces (alerts, EV strikes, analytics) and live in a dedicated
  // section so they slot cleanly next to the sibling's entries even if its
  // patch lands after ours.
  {
    heading: 'Parking',
    items: [
      { to: '/parking', label: 'Parking', icon: ParkingCircle, roles: ADMINS_AND_MANAGER },
      { to: '/parking/disputes', label: 'Disputes', icon: AlertTriangle, roles: ADMINS_AND_MANAGER },
      { to: '/parking/marketplace', label: 'Marketplace', icon: ArrowLeftRight, roles: ADMINS_AND_MANAGER },
      { to: '/parking/chargers', label: 'EV chargers', icon: Zap, roles: ADMINS_AND_MANAGER },
      { to: '/visitor-parking', label: 'Visitor parking', icon: UserCog, roles: ADMINS_AND_MANAGER },
      // Phase 3 parking surfaces. Sibling agent registers the /parking index +
      // CRUD pages in its own Sidebar PR; these three rows are the Phase-3
      // additive surfaces (alerts, EV strikes, analytics).
      { to: '/parking/wrong-parker', label: 'Wrong-parker alerts', icon: CarFront, roles: ADMINS_AND_MANAGER },
      { to: '/parking/ev-strikes', label: 'EV strikes', icon: ZapOff, roles: ADMINS_AND_MANAGER },
      { to: '/parking/analytics', label: 'Parking analytics', icon: BarChart3, roles: [...ADMINS_AND_MANAGER, 'president'] }
    ]
  },
  {
    heading: 'System',
    items: [
      // Audit log is read-only for all committee roles + accountant (per the
      // permission matrix). Admin-only mutations elsewhere remain admin-gated.
      // Reports — same role allow-list the server enforces (REPORT_ROLES).
      // Finance committee + admin can generate AGM-ready PDFs/Excel/CSV.
      { to: '/reports', label: 'Reports', icon: FileBarChart, roles: ['admin', 'super_admin', 'accountant', 'treasurer', 'president'] },
      { to: '/audit-log', label: 'Audit log', icon: ScrollText, roles: ['admin', 'super_admin', 'president', 'secretary', 'treasurer', 'accountant'] },
      { to: '/compliance', label: 'Compliance', icon: ShieldCheck, roles: ADMINS_ONLY },
      { to: '/settings', label: 'Settings', icon: Settings, roles: ADMINS_ONLY }
    ]
  }
];

const ROLE_LABEL: Record<string, string> = {
  super_admin: 'Super admin',
  society_admin: 'Society admin',
  society_staff: 'Society staff',
  admin: 'Society admin',
  accountant: 'Accountant',
  treasurer: 'Treasurer',
  secretary: 'Secretary',
  president: 'President',
  building_manager: 'Building manager',
  resident: 'Resident',
  guard: 'Guard'
};

interface SidebarProps {
  drawerOpen?: boolean;
  onClose?: () => void;
}

type Layout = 'mobile' | 'tablet' | 'desktop';

function readLayout(): Layout {
  if (typeof window === 'undefined') return 'desktop';
  if (window.matchMedia('(min-width: 1280px)').matches) return 'desktop';
  if (window.matchMedia('(min-width: 768px)').matches) return 'tablet';
  return 'mobile';
}

export function Sidebar({ drawerOpen = false, onClose }: SidebarProps): JSX.Element {
  const { logout, user } = useAuth();
  const { location } = useRouterState();
  const path = location.pathname;
  // White-label branding — when the tenant has uploaded a logo, the Sidebar
  // swaps the HoodDesk `LogoMark` for an `<img>` of the society logo. Falls
  // back to the default mark when no logo is set or the blob fetch fails.
  const branding = useBranding();
  const brandLogoUrl = useBrandLogoBlobUrl(branding.brand_logo_url);

  // Desktop-only user preference (persisted). Tablet always forces rail; mobile
  // always uses full-width inside the drawer regardless of this flag.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('vt-sidebar-collapsed') === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('vt-sidebar-collapsed', collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  // Track viewport bucket. Two media queries drive three states.
  const [layout, setLayout] = useState<Layout>(readLayout);
  useEffect(() => {
    const mqXl = window.matchMedia('(min-width: 1280px)');
    const mqMd = window.matchMedia('(min-width: 768px)');
    const update = (): void => setLayout(readLayout());
    mqXl.addEventListener('change', update);
    mqMd.addEventListener('change', update);
    return () => {
      mqXl.removeEventListener('change', update);
      mqMd.removeEventListener('change', update);
    };
  }, []);

  // Effective rail (icon-only) state:
  //   tablet → always rail
  //   desktop → user toggle
  //   mobile → full (rail not applicable, drawer fills 16rem)
  const isRail = layout === 'tablet' || (layout === 'desktop' && collapsed);

  // Visibility / transform:
  //   mobile  → fixed, slides in/out via drawerOpen
  //   tablet  → static rail, always visible
  //   desktop → static, always visible
  const isMobile = layout === 'mobile';

  return (
    <aside
      className={cn(
        'flex h-screen flex-col border-r border-vt-border bg-vt-surface transition-[width,transform] duration-vt ease-vt',
        // Mobile drawer: fixed overlay, slides on drawerOpen
        isMobile
          ? cn('fixed inset-y-0 left-0 z-40 w-64', drawerOpen ? 'translate-x-0' : '-translate-x-full')
          : // Tablet + desktop: static layout (in-flow), width depends on rail mode
            cn('static translate-x-0', isRail ? 'w-[72px]' : 'w-64')
      )}
      aria-label="Primary navigation"
      data-collapsed={isRail ? 'true' : 'false'}
      data-layout={layout}
    >
      {/* Brand */}
      <div className="flex h-14 items-center justify-between gap-vt-2 border-b border-vt-border px-vt-4">
        <Link
          to="/dashboard"
          onClick={isMobile ? onClose : undefined}
          className="flex items-center gap-vt-2 outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400 rounded-vt-sm"
        >
          {brandLogoUrl ? (
            <img
              src={brandLogoUrl}
              alt="Society logo"
              className="h-7 w-7 shrink-0 rounded-vt-sm object-contain"
            />
          ) : (
            <LogoMark size={28} />
          )}
          {isRail ? null : <Wordmark className="text-vt-16" />}
        </Link>
        {/* Close drawer — mobile only */}
        {isMobile ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            title="Close navigation"
            className="inline-flex h-7 w-7 items-center justify-center rounded-vt-sm text-vt-text-muted transition-colors duration-vt hover:bg-vt-surface-2 hover:text-vt-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
        {/* Collapse toggle — desktop only (tablet has forced rail) */}
        {layout === 'desktop' ? (
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="inline-flex h-7 w-7 items-center justify-center rounded-vt-sm text-vt-text-muted transition-colors duration-vt hover:bg-vt-surface-2 hover:text-vt-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400"
          >
            {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
          </button>
        ) : null}
      </div>

      {/* Nav — items are filtered by the caller's JWT role claim. Super-admin
          gets a cross-tenant nav (Tenants only for now); society admins,
          accountant + committee roles get the society-scoped nav filtered by
          role allow-list. */}
      <nav className="flex-1 overflow-y-auto px-vt-2 py-vt-3">
        <ul className="flex flex-col gap-vt-4">
          {(user?.role === 'super_admin' ? SUPER_ADMIN_NAV : NAV)
            .map((section) => ({
              ...section,
              items: section.items.filter(
                (item) => !item.roles || (user?.role && (item.roles as ReadonlyArray<string>).includes(user.role))
              )
            }))
            .filter((section) => section.items.length > 0)
            .map((section) => (
            <li key={section.heading}>
              {!isRail ? (
                <div className="px-vt-3 pb-vt-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-vt-text-soft">
                  {section.heading}
                </div>
              ) : null}
              <ul className="flex flex-col gap-[2px]">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = path === item.to || path.startsWith(`${item.to}/`);
                  return (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        onClick={isMobile ? onClose : undefined}
                        title={isRail ? item.label : undefined}
                        aria-label={isRail ? item.label : undefined}
                        className={cn(
                          'group relative flex h-9 items-center gap-vt-3 rounded-vt-sm px-vt-3 text-vt-14 transition-colors duration-vt ease-vt outline-none',
                          active
                            ? 'bg-vt-primary-100 text-vt-primary font-semibold'
                            : 'text-vt-text-muted hover:bg-vt-surface-2 hover:text-vt-text',
                          isRail && 'justify-center px-0'
                        )}
                        aria-current={active ? 'page' : undefined}
                      >
                        {/* active rail */}
                        <span
                          aria-hidden="true"
                          className={cn(
                            'absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-vt-primary transition-opacity duration-vt',
                            active ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        <Icon className="h-5 w-5 shrink-0" strokeWidth={1.75} aria-hidden="true" />
                        {isRail ? (
                          <span className="sr-only">{item.label}</span>
                        ) : (
                          <span className="truncate">{item.label}</span>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer — user identity + logout */}
      <div className="border-t border-vt-border p-vt-3">
        {user ? (
          isRail ? (
            <div className="mb-vt-2 flex items-center justify-center" title={`${ROLE_LABEL[user.role] ?? user.role}`}>
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-vt-primary-100 text-vt-primary">
                <CircleUserRound className="h-5 w-5" strokeWidth={1.75} />
              </span>
            </div>
          ) : (
            <div className="mb-vt-2 flex items-center gap-vt-3 rounded-vt-sm px-vt-2 py-vt-2">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-vt-primary-100 text-vt-primary">
                <CircleUserRound className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-vt-13 font-medium text-vt-text">
                  {user.phone ?? 'Signed in'}
                </div>
                <div className="mt-0.5 inline-flex items-center gap-1 rounded-vt-pill bg-vt-primary-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-vt-primary">
                  {ROLE_LABEL[user.role] ?? user.role}
                </div>
              </div>
            </div>
          )
        ) : null}
        <Button
          variant="ghost"
          size="sm"
          className={cn('w-full', isRail ? 'justify-center px-0' : 'justify-start')}
          onClick={() => {
            void logout();
          }}
          aria-label="Sign out"
          title="Sign out"
        >
          <LogOut className="h-4 w-4" />
          {isRail ? null : <span>Sign out</span>}
        </Button>
      </div>
    </aside>
  );
}
